import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Pencil } from 'lucide-react';
import type { ServiceTypeWithCatalog, WrapCoverageInput } from '@wrapforce/shared';
import type { VehicleRefSource } from '@wrapforce/shared/schemas/pricing';
import { useVehiclePanelPricing } from '@/features/settings/pricing-v2/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { useCreateInventoryItem, useInventoryAvailability, useInventoryItem, useUpdateInventoryItem } from '@/features/inventory/api';
import { useCreateMaterial } from '@/features/settings/api';
import { useLocaleFormatters } from '@/lib/format';

// Footage-comparable inventory units - the only ones a linear-feet job
// requirement (filmFtX10) can meaningfully be checked against. 'roll' /
// 'each' / 'bottle' items aren't tracked in feet, so the coverage check is
// skipped for those rather than producing a misleading comparison.
export const FOOTAGE_UNITS = new Set(['sqft', 'ft']);

type Material = ServiceTypeWithCatalog['materials'][number];

const STOCK_STYLE: Record<'ok' | 'low' | 'out', string> = {
  ok: 'bg-success/15 text-success',
  low: 'bg-warning/15 text-warning',
  out: 'bg-danger/15 text-danger',
};

function stockStatus(onHand: number, reorderPoint: number): 'ok' | 'low' | 'out' {
  if (onHand <= 0) return 'out';
  if (onHand <= reorderPoint) return 'low';
  return 'ok';
}

// The 13 panel keys seeded in panel_defs (per size class) — see
// supabase/migrations/0014_pricing_engine_seed.sql. Panel-diagram visual
// picker is explicitly out of scope for this phase (§29.10); a plain checkbox
// grid is what's specified.
const PANEL_KEYS = [
  'hood', 'roof', 'trunk_tailgate', 'front_bumper', 'rear_bumper', 'fenders',
  'doors', 'rockers', 'quarter_panels', 'mirrors', 'a_pillars', 'grille_surround', 'badges_delete',
] as const;

// Own top-level section in the wrap builder (was buried at the bottom of
// MaterialPicker) - "Full wrap" means all panels; "Partial panels" opens the
// per-panel checkbox grid for customers who only want certain parts serviced.
export function PanelCoverageSelector({
  workspaceId, coverage, onChangeCoverage, vehicleBaseFtX10, vehicleBaseMinutes, vehicleRef = null,
}: {
  workspaceId: string | null;
  coverage: WrapCoverageInput;
  onChangeCoverage: (coverage: WrapCoverageInput) => void;
  vehicleBaseFtX10: number;
  vehicleBaseMinutes: number;
  // The picked vehicle, for fetching its per-panel ft/minutes (with each
  // value's override level) and deep-linking to its Panel Pricing grid in
  // Settings - the pending draft is flushed on navigation (see NewQuotePage's
  // autosave), so following the link never loses the quote.
  vehicleRef?: { source: VehicleRefSource; id: string } | null;
}) {
  const { t } = useTranslation('quotes');
  const isPartial = coverage.kind === 'partial_panels';
  const selectedPanels = isPartial ? new Set(coverage.panels.map((p) => p.panelKey)) : new Set<string>();

  // Real per-panel values (vehicle override → workspace override → size-class
  // default). Only fetched once the partial-panel grid is open.
  const { data: panelCells } = useVehiclePanelPricing(
    isPartial ? workspaceId : null,
    vehicleRef?.source ?? null,
    vehicleRef?.id ?? null,
  );
  const cellByPanel = new Map((panelCells ?? []).map((c) => [c.panelKey as string, c]));
  const panelSettingsHref = vehicleRef ? `/pricing?tab=vehicles&vehicleId=${vehicleRef.id}&vehicleSource=${vehicleRef.source}` : null;
  const hasUnenteredSelected = isPartial
    && coverage.panels.some((p) => cellByPanel.get(p.panelKey)?.overrideLevel === 'default');

  function togglePanel(key: string) {
    const current = isPartial ? coverage.panels : [];
    const exists = current.some((p) => p.panelKey === key);
    const cell = cellByPanel.get(key);
    const nextPanels = exists
      ? current.filter((p) => p.panelKey !== key)
      : [...current, cell
          ? { panelKey: key, ftX10: cell.ftX10, minutes: cell.minutes }
          : approximatePanel(key, vehicleBaseFtX10, vehicleBaseMinutes)];
    onChangeCoverage({ kind: 'partial_panels', panels: nextPanels });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label>{t('pricing.material.coverageLabel')}</Label>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onChangeCoverage({ kind: 'full_wrap' })}
            className={`border px-3 py-1.5 text-xs font-semibold ${!isPartial ? 'border-accent bg-accent/10' : 'border-border hover:bg-surface-2'}`}
          >
            {t('pricing.material.fullWrap')}
          </button>
          <button
            type="button"
            onClick={() => onChangeCoverage({ kind: 'partial_panels', panels: isPartial ? coverage.panels : [] })}
            className={`border px-3 py-1.5 text-xs font-semibold ${isPartial ? 'border-accent bg-accent/10' : 'border-border hover:bg-surface-2'}`}
          >
            {t('pricing.material.partialPanels')}
          </button>
        </div>
      </div>

      {isPartial ? (
        <div className="flex flex-col gap-2 border border-border p-3">
          <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {PANEL_KEYS.map((key) => {
              const cell = cellByPanel.get(key);
              return (
                <label key={key} className="flex items-start gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selectedPanels.has(key)}
                    onChange={() => togglePanel(key)}
                  />
                  <span className="flex flex-col">
                    <span>{t(`pricing.material.panel.${key}`)}</span>
                    {cell ? (
                      <span className="text-[11px] text-muted">
                        {t('pricing.material.panelValue', { ft: (cell.ftX10 / 10).toFixed(1), min: cell.minutes })}
                        {cell.overrideLevel === 'default' ? (
                          <span className="ml-1 bg-warning/15 px-1 py-px text-[9.5px] font-bold uppercase tracking-wide text-warning">
                            {t('pricing.material.panelDefaultBadge')}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
          {!panelCells ? (
            <p className="text-xs italic text-muted">{t('pricing.material.panelApproxNote')}</p>
          ) : null}
          {hasUnenteredSelected ? (
            <div className="flex items-start gap-2 border border-warning/30 bg-warning/10 px-3 py-2 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              <span>{t('pricing.material.panelDefaultsNotice')}</span>
            </div>
          ) : null}
          {panelSettingsHref ? (
            <Link to={panelSettingsHref} className="w-fit text-xs font-semibold text-accent-ink underline">
              {t('pricing.material.panelSettingsLink')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function MaterialPicker({
  workspaceId, serviceTypeId, materials, materialId, onChangeMaterialId, itemId, onChangeItemId, requiredFtX10,
}: {
  workspaceId: string | null;
  serviceTypeId: string;
  materials: Material[];
  materialId: string;
  onChangeMaterialId: (id: string) => void;
  // The concrete inventory item being installed — lifted to the wrap builder
  // so its priceCentsPerUnit can drive the engine's film cost.
  itemId: string;
  onChangeItemId: (id: string) => void;
  // Linear feet x10 of film this specific vehicle/coverage/finish combo
  // needs, from the pricing engine's live result - null until a full
  // pricing input exists (e.g. no vehicle picked yet).
  requiredFtX10: number | null;
}) {
  const { t } = useTranslation('quotes');
  const activeMaterials = materials.filter((m) => m.isActive);
  const { data: availability } = useInventoryAvailability(workspaceId, activeMaterials.map((m) => m.id));
  const stockedMaterials = availability ? activeMaterials.filter((m) => availability[m.id]?.length) : [];
  const [addStockOpen, setAddStockOpen] = useState(false);

  // Only brands with a linked, in-stock inventory item are selectable for
  // a quote line - once availability loads, correct the selection away from
  // whatever the parent defaulted to (which may not be stocked).
  useEffect(() => {
    if (!availability) return;
    const firstStocked = stockedMaterials[0];
    if (!firstStocked) {
      if (materialId !== '') onChangeMaterialId('');
    } else if (!stockedMaterials.some((m) => m.id === materialId)) {
      onChangeMaterialId(firstStocked.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability]);

  const selectedBrand = stockedMaterials.find((m) => m.id === materialId)?.brand ?? '';
  const brandOptions = Array.from(new Set(stockedMaterials.map((m) => m.brand))).sort();
  const seriesOptionsForBrand = stockedMaterials.filter((m) => m.brand === selectedBrand);

  const brandItems = availability?.[materialId] ?? [];

  // Keep the item selection valid for the chosen brand - default to that
  // brand's first inventory item whenever the brand (or its items) change.
  useEffect(() => {
    if (!brandItems.some((i) => i.itemId === itemId)) {
      onChangeItemId(brandItems[0]?.itemId ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId, availability]);

  const selectedStock = brandItems.find((i) => i.itemId === itemId);

  const requiredFt = requiredFtX10 !== null ? requiredFtX10 / 10 : null;
  const shortfallFt =
    selectedStock && requiredFt !== null && FOOTAGE_UNITS.has(selectedStock.unit) && selectedStock.onHand < requiredFt
      ? requiredFt - selectedStock.onHand
      : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="material-brand">{t('pricing.material.materialLabel')}</Label>
          <button type="button" onClick={() => setAddStockOpen(true)} className="text-[11px] font-semibold text-accent-ink hover:underline">
            {t('pricing.material.addToInventory')}
          </button>
        </div>
        {!availability ? (
          <p className="text-xs text-muted">{t('pricing.material.loadingStocked')}</p>
        ) : stockedMaterials.length === 0 ? (
          <p className="text-xs text-muted">{t('pricing.material.noMaterialsLinked')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <select
              id="material-brand"
              value={selectedBrand}
              onChange={(e) => {
                const firstMatch = stockedMaterials.find((m) => m.brand === e.target.value);
                onChangeMaterialId(firstMatch?.id ?? '');
              }}
              className="h-9 border border-border bg-surface px-2 text-sm"
            >
              {brandOptions.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <select
              id="material-series"
              aria-label={t('pricing.material.seriesLabel')}
              value={materialId}
              onChange={(e) => onChangeMaterialId(e.target.value)}
              className="h-9 border border-border bg-surface px-2 text-sm"
            >
              {seriesOptionsForBrand.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.series || t('pricing.material.noSeries')}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {brandItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor="material-item">{t('pricing.material.itemLabel')}</Label>
          <select
            id="material-item"
            value={itemId}
            onChange={(e) => onChangeItemId(e.target.value)}
            className="h-9 border border-border bg-surface px-2 text-sm"
          >
            {brandItems.map((i) => (
              <option key={i.itemId} value={i.itemId}>
                {i.name}
              </option>
            ))}
          </select>
          {selectedStock ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className={`w-fit px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${STOCK_STYLE[stockStatus(selectedStock.onHand, selectedStock.reorderPoint)]}`}>
                {t('pricing.material.stock', { onHand: selectedStock.onHand, unit: selectedStock.unit })}
              </span>
              {requiredFt !== null && FOOTAGE_UNITS.has(selectedStock.unit) ? (
                <span className="w-fit bg-surface-2 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-muted">
                  {t('pricing.material.required', { requiredFt: requiredFt.toFixed(1), unit: selectedStock.unit })}
                </span>
              ) : null}
            </div>
          ) : null}
          {itemId ? <MaterialPriceInline workspaceId={workspaceId} itemId={itemId} /> : null}
        </div>
      ) : null}

      {selectedStock && shortfallFt !== null ? (
        <div className="flex items-start gap-2.5 border border-danger/30 bg-danger/10 px-3.5 py-3 text-sm text-danger">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">{t('pricing.material.shortageTitle', { unit: selectedStock.unit })}</span>
            <span>
              {t('pricing.material.shortageBody', {
                requiredFt: requiredFt?.toFixed(1),
                onHand: selectedStock.onHand,
                shortfallFt: shortfallFt.toFixed(1),
                unit: selectedStock.unit,
              })}
            </span>
            <Link to="/inventory" className="mt-1 w-fit font-semibold underline">
              {t('pricing.material.restockLink')}
            </Link>
          </div>
        </div>
      ) : null}

      <AddInventoryLinkDialog
        open={addStockOpen}
        onOpenChange={setAddStockOpen}
        workspaceId={workspaceId}
        serviceTypeId={serviceTypeId}
        materials={activeMaterials}
        defaultMaterialId={materialId || activeMaterials[0]?.id || ''}
        onCreated={(createdMaterialId, createdItemId) => {
          onChangeMaterialId(createdMaterialId);
          onChangeItemId(createdItemId);
        }}
      />
    </div>
  );
}

// Small inline pencil-to-popover so a shop can correct a material's sell
// price right from the quote builder instead of navigating to Inventory.
function MaterialPriceInline({ workspaceId, itemId }: { workspaceId: string | null; itemId: string }) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const { data: item } = useInventoryItem(workspaceId, itemId);
  const updateItem = useUpdateInventoryItem(workspaceId);
  const [editing, setEditing] = useState(false);
  const [priceDollars, setPriceDollars] = useState('');

  useEffect(() => {
    if (item) setPriceDollars((item.priceCentsPerUnit / 100).toFixed(2));
  }, [item]);

  if (!item) return null;

  async function handleSave() {
    const priceCentsPerUnit = priceDollars ? Math.round(Number(priceDollars) * 100) : 0;
    await updateItem.mutateAsync({ itemId, input: { priceCentsPerUnit } });
    toast.success(t('pricing.material.priceUpdated'));
    setEditing(false);
  }

  return (
    <div className="mt-1 flex items-center gap-1.5 text-xs text-muted">
      {editing ? (
        <>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
            className="h-7 w-20 px-1.5 text-xs"
            autoFocus
          />
          <Button type="button" size="sm" loading={updateItem.isPending} onClick={handleSave}>
            {t('common:actions.save')}
          </Button>
          <button type="button" onClick={() => setEditing(false)} className="text-[11px] font-semibold hover:underline">
            {t('common:actions.cancel')}
          </button>
        </>
      ) : (
        <>
          <span>{t('pricing.material.price', { price: formatMoney(item.priceCentsPerUnit), unit: item.unit })}</span>
          <button type="button" onClick={() => setEditing(true)} aria-label={t('pricing.material.editPrice')} className="text-muted hover:text-ink">
            <Pencil size={12} />
          </button>
        </>
      )}
    </div>
  );
}

function AddInventoryLinkDialog({
  open, onOpenChange, workspaceId, serviceTypeId, materials, defaultMaterialId, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string | null;
  serviceTypeId: string;
  materials: Material[];
  defaultMaterialId: string;
  onCreated: (materialId: string, itemId: string) => void;
}) {
  const { t } = useTranslation('quotes');
  const createItem = useCreateInventoryItem(workspaceId);
  const createMaterial = useCreateMaterial(workspaceId);
  const [linkMaterialId, setLinkMaterialId] = useState(defaultMaterialId);
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [addingSeries, setAddingSeries] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState('');
  const [name, setName] = useState('');
  const [initialQty, setInitialQty] = useState(0);
  const [reorderPoint, setReorderPoint] = useState(0);

  useEffect(() => {
    if (open) {
      setLinkMaterialId(defaultMaterialId);
      setAddingBrand(false);
      setNewBrandName('');
      setAddingSeries(false);
      setNewSeriesName('');
    }
  }, [open, defaultMaterialId]);

  // Mirror the picker's Brand + Series split so the series stays visible and
  // pickable here too (materials are brand+series combos in the catalog).
  const selectedBrand = materials.find((m) => m.id === linkMaterialId)?.brand ?? '';
  const brandOptions = Array.from(new Set(materials.map((m) => m.brand))).sort();
  const seriesOptionsForBrand = materials.filter((m) => m.brand === selectedBrand);

  // New brand / new series = new material (materials are brand+series
  // combos), same defaults as InventoryPage's BrandField.
  async function handleCreateBrand() {
    if (!newBrandName.trim()) return;
    const { id } = await createMaterial.mutateAsync({
      serviceTypeId,
      input: { brand: newBrandName.trim(), costPerSqftCents: 0, priceMultiplier: 1 },
    });
    setLinkMaterialId(id);
    setNewBrandName('');
    setAddingBrand(false);
    toast.success(t('pricing.material.brandAdded'));
  }

  async function handleCreateSeries() {
    if (!selectedBrand || !newSeriesName.trim()) return;
    const { id } = await createMaterial.mutateAsync({
      serviceTypeId,
      input: { brand: selectedBrand, series: newSeriesName.trim(), costPerSqftCents: 0, priceMultiplier: 1 },
    });
    setLinkMaterialId(id);
    setNewSeriesName('');
    setAddingSeries(false);
    toast.success(t('pricing.material.seriesAdded'));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!workspaceId || !name.trim() || !linkMaterialId) return;
    // MaterialPicker only renders inside the Wrap line builder, so every
    // material it can link is a wrap material - category is fixed accordingly.
    const created = await createItem.mutateAsync({ name: name.trim(), category: 'wrap', materialId: linkMaterialId, unit: 'sqft', initialQty, reorderPoint });
    toast.success(t('pricing.material.toastAdded'));
    onCreated(linkMaterialId, created.id);
    onOpenChange(false);
    setName('');
    setInitialQty(0);
    setReorderPoint(0);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('pricing.material.addInventoryDialog.title')} description={t('pricing.material.addInventoryDialog.description')}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="inv-link-brand">{t('pricing.material.addInventoryDialog.material')}</Label>
              <button
                type="button"
                onClick={() => {
                  setAddingBrand((v) => !v);
                  setAddingSeries(false);
                }}
                className="text-[11px] font-semibold text-accent-ink hover:underline"
              >
                {addingBrand ? t('common:actions.cancel') : t('pricing.material.addNewBrand')}
              </button>
            </div>
            <select
              id="inv-link-brand"
              value={selectedBrand}
              onChange={(e) => {
                const firstMatch = materials.find((m) => m.brand === e.target.value);
                setLinkMaterialId(firstMatch?.id ?? '');
              }}
              className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm"
            >
              {brandOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="inv-link-series">{t('pricing.material.seriesLabel')}</Label>
              <button
                type="button"
                onClick={() => {
                  setAddingSeries((v) => !v);
                  setAddingBrand(false);
                }}
                className="text-[11px] font-semibold text-accent-ink hover:underline"
              >
                {addingSeries ? t('common:actions.cancel') : t('pricing.material.addNewSeries')}
              </button>
            </div>
            <select
              id="inv-link-series"
              value={linkMaterialId}
              onChange={(e) => setLinkMaterialId(e.target.value)}
              className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm"
            >
              {seriesOptionsForBrand.map((m) => (
                <option key={m.id} value={m.id}>{m.series || t('pricing.material.noSeries')}</option>
              ))}
            </select>
          </div>
        </div>
        {addingBrand ? (
          <div className="flex gap-2">
            <Input
              placeholder={t('pricing.material.newBrandPlaceholder')}
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateBrand();
                }
              }}
              className="flex-1"
            />
            <Button type="button" size="sm" disabled={!newBrandName.trim()} loading={createMaterial.isPending} onClick={handleCreateBrand}>
              {t('common:actions.save')}
            </Button>
          </div>
        ) : null}
        {addingSeries ? (
          <div className="flex gap-2">
            <Input
              placeholder={t('pricing.material.newSeriesPlaceholder')}
              value={newSeriesName}
              onChange={(e) => setNewSeriesName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateSeries();
                }
              }}
              className="flex-1"
            />
            <Button type="button" size="sm" disabled={!selectedBrand || !newSeriesName.trim()} loading={createMaterial.isPending} onClick={handleCreateSeries}>
              {t('common:actions.save')}
            </Button>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="inv-link-name">{t('pricing.material.addInventoryDialog.itemName')}</Label>
          <Input id="inv-link-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('pricing.material.addInventoryDialog.itemNamePlaceholder')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="inv-link-qty">{t('pricing.material.addInventoryDialog.onHand')}</Label>
            <Input id="inv-link-qty" type="number" step="0.01" value={initialQty} onChange={(e) => setInitialQty(Number(e.target.value))} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="inv-link-reorder">{t('pricing.material.addInventoryDialog.reorderAt')}</Label>
            <Input id="inv-link-reorder" type="number" step="0.01" value={reorderPoint} onChange={(e) => setReorderPoint(Number(e.target.value))} />
          </div>
        </div>
        <Button type="submit" loading={createItem.isPending} disabled={!name.trim() || !linkMaterialId} className="mt-1 w-full">
          {t('pricing.material.addInventoryDialog.submit')}
        </Button>
      </form>
    </Dialog>
  );
}

// CLIENT-PREVIEW APPROXIMATION ONLY — not a source of truth. There is no
// panel-defs endpoint exposed to the client, so we assume each checked panel
// contributes an even 1/6th fraction of the vehicle's full base ft/minutes
// (a rough stand-in for "an average panel is ~1/6 of a full wrap"). The real
// number comes from the server's price-preview call and, ultimately, the
// recompute-on-save resolver (server/src/modules/pricing/resolve.ts), which
// has real panel_defs data keyed by panel_key + size_class.
const PANEL_FRACTION_DENOMINATOR = 6;
export function approximatePanel(panelKey: string, vehicleBaseFtX10: number, vehicleBaseMinutes: number) {
  return {
    panelKey,
    ftX10: Math.round(vehicleBaseFtX10 / PANEL_FRACTION_DENOMINATOR),
    minutes: Math.round(vehicleBaseMinutes / PANEL_FRACTION_DENOMINATOR),
  };
}
